import { Archive, ArchiveRestore, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  familyAuthoringIsOpen,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { formatDate } from "@/lib/format";
import {
  ASSET_TYPE_GROUP_LABELS,
  ASSET_TYPE_LABELS,
  assetTypesForSection,
  libraryAssetHref,
  type LibrarySectionSlug,
} from "@/lib/librarySections";
import { Notice } from "@/ui/Notice";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { IconButton } from "@/ui/IconButton";
import { Tooltip } from "@/ui/Tooltip";
import { EmptyState, LoadingState, StaleDataNotice } from "@/ui/states";

import { AssetStateBadges } from "./AssetStateBadges";
import { CatalogGroup } from "./CatalogGroup";
import { CatalogRow } from "./CatalogRow";
import { LibrarySectionHeader } from "./LibrarySectionHeader";
import { LifecycleDialog, type LifecycleMode } from "./LifecycleDialog";
import { NamedReadFailureState } from "./named/NamedReadFailure";
import { NewAlertMatrixDialog } from "./NewAlertMatrixDialog";
import { NewSpeechProfileDialog } from "./named/NewSpeechProfileDialog";
import {
  GENERIC_DIALOG_TYPES,
  NewAssetDialog,
  isCreatableAssetType,
  type CreatableAssetType,
} from "./NewAssetDialog";

/**
 * Where a create control leads — one value, never a set of flags.
 *
 * A section may offer more than one, and `safety-paths` is the first that does
 * (#494): the safety track is parts-shaped and reaches `NewAssetDialog`, the
 * alert matrix is typed content and reaches its own dialog. Holding the chosen
 * destination rather than a `creating` boolean is what makes the two mutually
 * exclusive without a rule anybody has to remember.
 */
type CreateDestination =
  | { kind: "parts"; assetType: CreatableAssetType }
  | { kind: "alert_matrix" }
  | { kind: "speech_profile" };

/**
 * Why this section has no create control, stated rather than left as an absence.
 *
 * A missing button is not an explanation. Ratified contract §2.2 keeps
 * arbitrary questionnaire authoring disabled until validity, licensing, scoring
 * and versioning contracts are approved, and a clinician who came here to add
 * an instrument is owed that sentence instead of a screen where the control
 * they remember has silently disappeared.
 */
const GOVERNED_INVENTORY_NOTICE =
  "Questionnaires are a governed family. Their approved inventory is seeded from the clinical catalog, so new instruments arrive through clinical and licensing review rather than being authored here. Existing instruments stay readable, and projects keep pinning them at exact versions.";

/*
 * THE SIX-TRACK COLUMN GRID IS GONE (#908).
 *
 * `COLUMNS` declared `Asset · State · Version · Author · Updated · Adoption` as
 * independent tracks and `ColumnHeader` labelled them, gated at `@5xl` (64rem)
 * on the CARD's width. Beside the application sidebar and the library rail that
 * card is about 712px at the repository's 1280-wide baseline — under the gate —
 * so what a clinician actually saw at every ordinary width was the stacked
 * fallback: four lines and roughly 147px per asset, the "tall generic cards"
 * the ticket names. The tracks were never reached; widening them was not the
 * fix, because the width was never there to give.
 *
 * `CatalogRow` puts the same six facts on one flowing metadata line, which fits
 * the width that exists. Nothing is dropped to make room: version, state,
 * author, updated date and adoption count are all still on the row, and the
 * reference's own rows read the same way (`v6.0 · base · updated by Dana Katz ·
 * used by 2 projects`). The column header goes with the columns — a header for
 * a grid that no longer exists would name tracks nothing renders.
 */

/**
 * Served order, except that a pathway variant sits directly under its parent.
 *
 * Variant identity is explicit (`parentPathwayId`, never a name), so the
 * adjacency is a join, not a guess. A variant whose parent is not in the
 * filtered list — archived parent while the variant is active, say — keeps
 * its served position rather than disappearing.
 */
export function orderVariantsUnderParents(
  items: LibraryAsset[],
): LibraryAsset[] {
  const variantsByParent = new Map<string, LibraryAsset[]>();
  for (const asset of items) {
    if (asset.assetType === "pathway_variant") {
      const siblings = variantsByParent.get(asset.parentPathwayId) ?? [];
      siblings.push(asset);
      variantsByParent.set(asset.parentPathwayId, siblings);
    }
  }

  const parentIds = new Set(
    items
      .filter((asset) => asset.assetType === "pathway")
      .map((asset) => asset.id),
  );

  const ordered: LibraryAsset[] = [];
  for (const asset of items) {
    if (
      asset.assetType === "pathway_variant" &&
      parentIds.has(asset.parentPathwayId)
    ) {
      continue; // Re-inserted directly under its parent below.
    }
    ordered.push(asset);
    if (asset.assetType === "pathway") {
      ordered.push(...(variantsByParent.get(asset.id) ?? []));
    }
  }
  return ordered;
}

/**
 * One asset, compact (#908).
 *
 * The TYPE is no longer a second line under the name. It is not dropped — it is
 * stated once by the heading above the rows instead of once per row: a
 * single-type section's `h1` already names it ("BG sets", "Summary"), and a
 * multi-type section names each group's type on the group's own heading. A row
 * repeating what its heading just said is a line of height bought for nothing,
 * and the reference's rows carry no type either.
 */
function AssetRow({
  asset,
  onLifecycle,
  missingWriteReason,
  missingRestoreReason,
}: {
  asset: LibraryAsset;
  onLifecycle: (asset: LibraryAsset, mode: LifecycleMode) => void;
  missingWriteReason: string | null;
  /**
   * Why this caller cannot RESTORE, or `null`. Separate from the write reason
   * because the service gates the two directions differently (#647): archive
   * is `library.write`, unarchive is the admin/operations `library.restore`.
   */
  missingRestoreReason: string | null;
}) {
  const mode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  const refusal = lifecycleRefusal(
    asset,
    mode === "unarchive" ? missingRestoreReason : missingWriteReason,
  );
  const label =
    mode === "archive"
      ? `Archive ${asset.name}`
      : `Unarchive ${asset.name}`;
  const adoption = asset.capabilities.adoptionCount;

  return (
    <CatalogRow
      // #615's one derivation of an asset's library address from what the
      // contract states about it — its `assetType` — rather than from the rail
      // section it happens to be rendered in.
      to={libraryAssetHref(asset.assetType, asset.id)}
      name={asset.name}
      titleAdornment={<AssetStateBadges capabilities={asset.capabilities} />}
      // EVERY FACT NAMES ITSELF. The six-track grid had a column header saying
      // which was the author and which the date; one flowing line has none, and
      // `v1 · seed-importer · Sep 7, 2026 · 0 projects` leaves a clinician to
      // guess whether the principal created or last edited the asset and
      // whether the count is adoptions or versions. The qualifiers are the
      // contract's own words — `createdBy` is "the principal that created the
      // asset", `updatedAt` is "last head change", `adoptionCount` is "how many
      // projects pin any version" — and the reference labels its own line the
      // same way (`… updated by Dana Katz · used by 2 projects`).
      //
      // ONE TEXT NODE PER FACT, qualifier included. Each is still its own
      // element — which is what keeps this four readable facts for a screen
      // reader rather than one run-on sentence — but the qualifier and its
      // value are not split across nested nodes, so the fact reads and matches
      // as the single phrase it is.
      meta={[
        <span key="version" className="font-mono text-sm text-foreground">
          {asset.version}
        </span>,
        <span key="author" className="min-w-0 truncate">
          {`Created by ${asset.createdBy}`}
        </span>,
        <span key="updated">{`Updated ${formatDate(asset.updatedAt)}`}</span>,
        <span key="adoption">
          {`Used by ${adoption} ${adoption === 1 ? "project" : "projects"}`}
        </span>,
      ]}
      actions={
        // The action never navigates: DataRow paints it above the stretched
        // row link, so the browser hit-tests the button and nothing else —
        // the structural fix for the prototype's delete-navigates defect.
        <Tooltip content={refusal ?? label}>
          <IconButton
            label={label}
            showTooltip={false}
            disabled={refusal !== null}
            icon={
              mode === "archive" ? (
                <Archive className="h-4 w-4" />
              ) : (
                <ArchiveRestore className="h-4 w-4" />
              )
            }
            onClick={() => onLifecycle(asset, mode)}
          />
        </Tooltip>
      }
    />
  );
}

/**
 * One rail section of the shared clinical library: the assets of that
 * section's types, their provenance, and their lifecycle actions.
 *
 * There is no delete control anywhere on this surface — the contract has no
 * delete route for library assets, and this catalog does not invent one.
 * Content is neither fetched nor rendered here: catalog reads carry no content
 * by contract. Creation is the one write that belongs on a catalog rather than
 * on an asset (#33): there is no asset yet to open.
 */
export function LibraryCatalog({
  section,
  sectionLabel,
  sectionDescription,
}: {
  section: LibrarySectionSlug;
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [lifecycle, setLifecycle] = useState<{
    asset: LibraryAsset;
    mode: LifecycleMode;
  } | null>(null);
  const [creating, setCreating] = useState<CreateDestination | null>(null);
  const assets = useLibraryAssetsQuery(showArchived);
  // The service's own policy document, cached like the part manifest: it
  // describes the SERVICE rather than any asset, so it neither blocks the
  // catalog nor re-seeds anything.
  const registry = useLibraryPlacementRegistryQuery();
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  // Keyed to the CURRENT toggle state: the paused query this control recovers
  // is whichever one the catalog is rendering right now.
  const retryAssets = useRetryPausedQuery(queryKeys.libraryAssets(showArchived));

  const types = assetTypesForSection(section);
  const items = orderVariantsUnderParents(
    (assets.data?.items ?? []).filter((asset) =>
      types.includes(asset.assetType),
    ),
  );

  // Sections whose types belong to #36 still get no create control rather than
  // a disabled one: the reason is not something this clinician can fix, and a
  // permanently dead button is worse than an absent one.
  //
  // QUESTIONNAIRES LEFT THAT LIST IN #368, AND THE ALERT MATRIX IN #494. Both
  // are typed content, so neither is creatable through `NewAssetDialog`'s parts
  // form and `creatableType` correctly stays null for the questionnaire
  // section — but each now has a create destination of its own, and without one
  // the only asset of that family in existence is a seeded one that refuses
  // every content write.
  // THE TYPES THIS DIALOG ACTUALLY OFFERS, not every parts-shaped one (#863).
  //
  // `CREATABLE_ASSET_TYPES` means "parts-shaped and authorable", which is still
  // true of `main_concern`, the two Pathway types and `summary_prompt` — but
  // each of those has a canonical dialog of its own, and this component would
  // open the GENERIC one preselected on the type its own Type control refuses
  // to offer. That is the latent widening `GENERIC_DIALOG_TYPES` records, and
  // for a Summary prompt it is not latent: this dialog's Pack placement field
  // is the free-text coordinate the committed MOD/NII equivalence contract
  // forbids for that family. Reading the offered list closes it for all three
  // at once, and none of the three routes here any more.
  const creatableType: CreatableAssetType | null =
    types.find(
      (assetType): assetType is CreatableAssetType =>
        isCreatableAssetType(assetType) &&
        (GENERIC_DIALOG_TYPES as readonly string[]).includes(assetType),
    ) ?? null;
  const createRefusal = write.reason;

  // Every destination this section can create into, in served type order.
  //
  // `safety-paths` is the first section with TWO, and that is what this shape
  // exists for. The dialogs used to render under independent `creating && …`
  // conditions, and `creatableType` is already non-null here (`safety_track`) —
  // so a third condition in that same shape would have mounted BOTH dialogs at
  // once. One destination is held in state instead of a boolean, so the choice
  // is exclusive by construction rather than by three booleans agreeing.
  const destinations: Array<{
    key: string;
    typeLabel: string;
    destination: CreateDestination;
  }> = [
    ...(creatableType !== null
      ? [
          {
            key: creatableType,
            typeLabel: ASSET_TYPE_LABELS[creatableType],
            destination: { kind: "parts", assetType: creatableType } as const,
          },
        ]
      : []),
    ...(types.includes("alert_matrix")
      ? [
          {
            key: "alert_matrix",
            typeLabel: ASSET_TYPE_LABELS.alert_matrix,
            destination: { kind: "alert_matrix" } as const,
          },
        ]
      : []),
    // #870's destination, on `alert_matrix`' own rule and for the same reason.
    // A speech profile is TYPED content — two per-language documents — so it is
    // deliberately absent from `CREATABLE_ASSET_TYPES` and unreachable through
    // `NewAssetDialog`'s parts form, whose free-text part key and raw-JSON field
    // are exactly what this family's named editor exists to remove. It is the
    // SECOND destination in Rules, which is what groups that section: `Language
    // rules` and `Speech profiles` each get their own heading and their own
    // create control, instead of one "New asset" button whose Type control
    // would have to reach a family it cannot author.
    ...(types.includes("speech_profile")
      ? [
          {
            key: "speech_profile",
            typeLabel: ASSET_TYPE_LABELS.speech_profile,
            destination: { kind: "speech_profile" } as const,
          },
        ]
      : []),
  ];

  // With two destinations each control names the ONE it opens: there is no
  // silent default to whichever type is found first, which is the whole of the
  // ticket's disambiguation requirement. With one, the labels stay exactly what
  // they were — "New asset" for the parts dialog, whose own Type control still
  // reaches every creatable type, and the type's own name for typed content.
  const disambiguated = destinations.length > 1;

  function createControl(entry: (typeof destinations)[number]): ReactNode {
    const label =
      disambiguated || entry.destination.kind !== "parts"
        ? `New ${entry.typeLabel.toLowerCase()}`
        : "New asset";
    return (
      <Tooltip
        key={entry.key}
        content={
          createRefusal ??
          (disambiguated
            ? `Create a new ${entry.typeLabel.toLowerCase()} in ${sectionLabel}`
            : `Create a new asset in ${sectionLabel}`)
        }
      >
        <Button
          variant="outline"
          size="sm"
          disabled={createRefusal !== null}
          onClick={() => setCreating(entry.destination)}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          {label}
        </Button>
      </Tooltip>
    );
  }

  /**
   * WHERE THE CREATION CONTROLS GO — the section header, or each group (#908).
   *
   * The reference puts them in both places and the rule is which thing the
   * control belongs to. `/library/pathways` and `/library/summary` list ONE kind
   * of asset, so their single action sits at the top right beside the section's
   * heading ("New pathway parent", "New section") — both now have named
   * catalogs of their own (#643, #863) and neither reaches this component, but
   * the rule they established is what the remaining sections follow. `/library/bg-sets` lists two
   * kinds under two headings, and neither of its actions belongs to the section
   * as a whole — so `New set file` sits on the Set files heading and `New
   * domain` on the Domains heading, and the section header carries no creation
   * control at all.
   *
   * TWO CONDITIONS, not one. The section must list more than one kind of asset
   * — otherwise there is nothing to separate — AND offer more than one place to
   * create, which is what makes a group-level action mean something. Only
   * `safety-paths` satisfies both: #494 gave it two identically-shaped buttons
   * side by side in one toolbar, with each label the only thing saying which
   * type it created, and grouping puts each beside the rows it creates into.
   *
   * `pathways` deliberately fails the second condition. It lists two types, but
   * a variant is created from its family rather than from a section-level
   * control, so splitting it would separate every variant from the parent
   * `orderVariantsUnderParents` places it under — losing the adjacency to gain a
   * heading nothing acts on.
   */
  /**
   * GROUPS NEED ROWS, and that is what keeps the controls reachable. A grouped
   * section renders its group headings only where it renders rows, so tying the
   * controls to the groups alone would have taken `New safety track` and `New
   * alert matrix` off the screen for the whole of the initial load and
   * permanently on a failed read — controls that do not depend on that read at
   * all. With `items.length` in the condition, a section with nothing on screen
   * is simply not grouped, so its controls are in the header exactly as an
   * ungrouped section's are, and they move into the group headings once there
   * are rows for those headings to belong to.
   */
  const grouped = types.length > 1 && destinations.length > 1 && items.length > 0;
  const createControls =
    grouped || destinations.length === 0 ? null : (
      <>{destinations.map(createControl)}</>
    );

  /**
   * One group per asset type the SECTION lists, in the contract's own order.
   *
   * Derived from `types` rather than from `destinations`, so every row lands in
   * a group: a type that offers no creation control still gets its heading and
   * its rows, and only the action is absent. Each group filters `items` rather
   * than re-sorting them, so served order survives inside a group. Ungrouped
   * sections never reach this.
   */
  const groups = grouped
    ? types.map((assetType) => {
        const entry = destinations.find((candidate) =>
          candidate.destination.kind === "parts"
            ? candidate.destination.assetType === assetType
            : // A TYPED destination is matched by its own key, not by a
              // literal. This read `assetType === "alert_matrix"` while the
              // matrix was the only typed destination, so #870's speech
              // profile — the second — resolved into the alert-matrix group
              // instead. The consequence was not a misplaced button: Rules
              // groups as soon as it holds one speech profile, and the group's
              // action was then `null`, so `New speech profile` vanished from
              // the product the moment the first one existed. `key` is the
              // destination's own asset type for every typed destination, so
              // the next one cannot repeat this.
              candidate.key === assetType,
        );
        return {
          key: assetType,
          title: ASSET_TYPE_GROUP_LABELS[assetType],
          actions: entry ? createControl(entry) : null,
          rows: items.filter((asset) => asset.assetType === assetType),
        };
      })
    : [];

  // READ, never restated: the registry publishes which families refuse
  // authoring and the service refuses the write against the same set, so this
  // surface cannot offer a control whose submit is refused — nor withhold one
  // the service would accept. Which INSTRUMENTS are approved is a different
  // question with a different answer per row (`capabilities.licensedInventory`),
  // and the browser holds no roster of those either.
  const governed = types.some(
    (assetType) => !familyAuthoringIsOpen(registry.data, assetType),
  );

  const headerActions = (
    <>
      {createControls}
      {/* A toggle button, so its state lives in `aria-pressed` and in the
          pressed visual — and the label stays put. The APG button pattern is
          explicit that a toggle's label must not change with its state: a
          flipped label under `aria-pressed` announces "Hide archived,
          pressed", two encodings of one state that read as contradictory.
          The audit's PW-LIB-09 (#648) was that the state was unknowable, and
          the half that was actually missing was the VISUAL: `outline` in both
          modes shows a sighted clinician nothing. `variant` now follows the
          same `showArchived` that drives the server query, exactly as every
          other `aria-pressed` control in this application does
          (`AlertMatrixEditor`, `MainConcernCatalog`). */}
      <Button
        variant={showArchived ? "primary" : "outline"}
        size="sm"
        aria-pressed={showArchived}
        onClick={() => setShowArchived((value) => !value)}
      >
        Show archived
      </Button>
    </>
  );

  return (
    <>
      {/* #908: the section's own breadcrumb, heading, description and action
          area. `LibraryRoute` no longer renders a page-wide "Clinical library"
          banner above the rail, so this is the page's `h1` — and the five
          sections this component serves each get one, where before only the two
          canonical catalogs had a heading of their own. */}
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={headerActions}
      />
      <section aria-label={`${sectionLabel} library assets`} className="space-y-4">
      {governed ? <Notice>{GOVERNED_INVENTORY_NOTICE}</Notice> : null}

      {/* A held refresh over cached rows is still a failed refresh: a parked
          retry keeps `status: "success"` with `isError: false`, so testing the
          settled half alone left rows of unknown age presenting as current.
          Same reasoning, at length, in `MainConcernCatalog`. */}
      {assets.data ? (
        <StaleDataNotice
          updatedAt={assets.dataUpdatedAt}
          onRetry={retryAssets}
          visible={
            assets.isPaused ||
            (assets.isError && !isTerminalQueryError(assets.error))
          }
        />
      ) : null}

      {assets.isPaused && assets.data === undefined ? (
        // A held read with nothing in hand, which `isPending` cannot express:
        // it stays `"pending"` while nothing is in flight, so the skeleton
        // below would render permanently with no recovery control.
        <NamedReadFailureState
          failure={assets.failureReason}
          isPaused
          retry={retryAssets}
          subject="Library assets"
          title="Library assets could not be loaded"
          description="The Editor Service did not return the shared clinical library."
          deniedDescription="You are not authorized to read the shared clinical library."
          offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
        />
      ) : assets.isPending ? (
        <LoadingState label={`Loading ${sectionLabel} assets`} rows={4} />
      ) : assets.isError &&
        (!assets.data || isTerminalQueryError(assets.error)) ? (
        <QueryErrorState
          error={assets.error}
          subject="Library assets"
          title="Library assets could not be loaded"
          description="The Editor Service did not return the shared clinical library."
          deniedDescription="You are not authorized to read the shared clinical library."
          onRetry={() => void assets.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={`No assets in ${sectionLabel} yet`}
          description={
            showArchived
              ? "Nothing of this section's types exists in the library."
              : "Nothing active is in this section. Archived assets stay hidden until shown."
          }
          // The empty state is where creation matters most, and where the
          // header control is easiest to miss. An empty section is never
          // grouped (see `grouped` above), so these are the same controls the
          // header is already showing.
          {...(createControls ? { action: createControls } : {})}
        />
      ) : grouped ? (
        <div className="space-y-6">
          {groups.map((group) => (
            <CatalogGroup
              key={group.key}
              title={group.title}
              {...(group.actions ? { actions: group.actions } : {})}
            >
              {group.rows.length === 0 ? (
                // Said, not left blank: an empty group under a heading a
                // clinician can create into is a different fact from a section
                // with nothing in it, and only one of the two is fixed by the
                // control beside the heading.
                <p className="rounded-md border border-border px-5 py-3 text-sm text-muted-foreground">
                  {showArchived
                    ? `No ${group.title.toLowerCase()} exist in the library.`
                    : `No active ${group.title.toLowerCase()} are in this section. Archived assets stay hidden until shown.`}
                </p>
              ) : (
                <Card className="@container overflow-hidden">
                  {group.rows.map((asset) => (
                    <AssetRow
                      key={asset.id}
                      asset={asset}
                      onLifecycle={(target, mode) =>
                        setLifecycle({ asset: target, mode })
                      }
                      missingWriteReason={write.reason}
                      missingRestoreReason={restore.reason}
                    />
                  ))}
                </Card>
              )}
            </CatalogGroup>
          ))}
        </div>
      ) : (
        <Card className="@container overflow-hidden">
          {items.map((asset) => (
            <AssetRow
              key={asset.id}
              asset={asset}
              onLifecycle={(target, mode) =>
                setLifecycle({ asset: target, mode })
              }
              missingWriteReason={write.reason}
              missingRestoreReason={restore.reason}
            />
          ))}
        </Card>
      )}

      {lifecycle ? (
        <LifecycleDialog
          asset={lifecycle.asset}
          mode={lifecycle.mode}
          onClose={() => setLifecycle(null)}
        />
      ) : null}

      {/* ONE destination, so one dialog: the ladder is exhaustive over
          `CreateDestination` and returns a single element, which is what makes
          "never both dialogs at once" a property of the code rather than of
          the conditions happening to be mutually exclusive. */}
      {creating === null ? null : creating.kind === "alert_matrix" ? (
        <NewAlertMatrixDialog onClose={() => setCreating(null)} />
      ) : creating.kind === "speech_profile" ? (
        <NewSpeechProfileDialog onClose={() => setCreating(null)} />
      ) : (
        <NewAssetDialog
          initialAssetType={creating.assetType}
          onClose={() => setCreating(null)}
        />
      )}
      </section>
    </>
  );
}
